import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

const faqs = [
  {
    question: "What is NameExplorer?",
    answer: "NameExplorer is a comprehensive platform that helps you discover the meanings of Nepali names, find name matches, and explore traditional Nepali wisdom through quotes."
  },
  {
    question: "How accurate are the name meanings?",
    answer: "Our name meanings are carefully researched from authentic Nepali sources and verified by language experts. We regularly update our database to ensure accuracy."
  },
  {
    question: "How does the name matching feature work?",
    answer: "Our name matching algorithm considers cultural significance, sound harmony, and traditional compatibility factors to suggest names that complement each other well."
  },
  {
    question: "Can I suggest new names or meanings?",
    answer: "Yes! We welcome community contributions. Use the 'Report Error' button on any name page to suggest corrections or new information."
  },
  {
    question: "How can I download and share quotes?",
    answer: "Each quote has a download button that creates a beautifully designed image. You can also share quotes directly to social media platforms."
  },
  {
    question: "Are the quotes authentic Nepali wisdom?",
    answer: "Yes, our quotes are collected from traditional Nepali literature, cultural texts, and verified sources of Nepali wisdom."
  },
  {
    question: "Is NameExplorer available in Nepali language?",
    answer: "Currently, we provide meanings in both English and Nepali. A full Nepali interface is under development."
  },
  {
    question: "How often is the content updated?",
    answer: "We regularly update our database with new names, meanings, and quotes. Our content team adds new material weekly."
  }
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="py-16 bg-gradient-to-br from-purple-50 to-pink-50">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-600">
            Find answers to common questions about NameExplorer
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-lg shadow-sm overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50"
              >
                <span className="font-medium text-gray-900">{faq.question}</span>
                <ChevronDown
                  className={`transform transition-transform ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                  size={20}
                />
              </button>

              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="px-6 pb-4"
                  >
                    <p className="text-gray-600">{faq.answer}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}